import { Input, Modal, Notification } from 'Components';
import TInfoText from 'Contexts/TInfoText/TInfoText';
import { Form, Formik, FormikHelpers } from 'formik';
import { useModal } from 'Hooks/useModal';
import { useStores } from 'Hooks/useStore';
import { useTranslation } from 'react-i18next';
import {
	ReportModalAction,
	ReportModalsPropsBase,
	ReportModalValue
} from './ReportModals.types';
import { schema } from 'Pages/Report/EditReport/schema';
import { AxiosErrorResponse } from 'app-types';
import { getFormErrors } from 'Helpers/api';
import { CloneReportRequest } from 'Services/Api/Reports/Types';
import CommonModal from 'PhishingManager/components/Modal';
import ModalHeader from './ModalHeader';
import { useDashboardContext } from 'Pages/Dashboard/DashboardContext';

const cloneSchema = schema.pick(['name']);

const PATH = 'reports.tabs.saved.cloneReportModal';

function CloneReportModal(props: ReportModalsPropsBase) {
	const { t } = useTranslation();
	const { setToastInfo } = useDashboardContext();
	const { modal, hideModal } = useModal();
	const { reportStore } = useStores();

	const modalValue = modal as ReportModalValue;

	if (!modalValue || modalValue.name !== ReportModalAction.Clone) {
		return null;
	}

	const onSubmit = async (
		values: CloneReportRequest,
		helpers: FormikHelpers<CloneReportRequest>
	) => {
		try {
			await reportStore.cloneReport(values);
			hideModal();
			props.refresh();
			setToastInfo?.({ type: "success", message: t(`${PATH}.successNotification`), icon: "check" })
		} catch (e) {
			const error = e as AxiosErrorResponse;
			const errors = getFormErrors(
				error.response?.data.error,
				t,
				cloneSchema.describe().fields
			);
			helpers.setErrors(errors);
		}
	};

	const initialValues = {
		id: modalValue.value.id,
		name: ''
	};

	return (
		<Formik
			onSubmit={onSubmit}
			initialValues={initialValues}
			validationSchema={cloneSchema}
		>
			{({ submitForm, isSubmitting, isValid, dirty }) => (
				<CommonModal
					visible
					modalTitle={<ModalHeader title={t('clone')} desc={modalValue.value.name} />}
					confirmText={t('clone')}
					cancelText={t('cancel')}
					iconType={null}
					showConfirmBtn={true}
					confirmBtnDisabled={isSubmitting || !(isValid && dirty)}
					onClose={hideModal}
					onConfirm={submitForm}
					parentClass='report-modal'
				>
					<Form>
						<Input.FormikField
							name="name"
							label={t(`report.fields.name`)}
							placeholder={t(`${PATH}.placeholder`)}
							required
							marginBottom={false}
							autoFocus
						/>
					</Form>
				</CommonModal>
			)}
		</Formik>
	);
}

export default CloneReportModal;